import React, { ForwardedRef, useImperativeHandle } from 'react';
import { FormGroup } from 'react-bootstrap';
import DragAndDrop from './DragAndDrop';
import { BLACKLIST_FILE_EXTENSIONS, ERROR_MESSAGE, FIFTEEN_MEGABYTE } from './utility/constants';
import { fieldHasErrors, InlineHelper } from '../../../helpers';
import useFileUploadInternal from './hooks/useFileUploadInternal';
import { FileUploadProps, FileUploadRef } from './types';
import { sharedDefaultProps } from './utility/sharedPropTypes';
import { inputDefaultProps } from '../../../props/inputProps';
import FileUploadFilesList from './FileUploadFilesList';
import Label from '../../utility/Label';
import Helpblock from '../../utility/Helpblock';
import FormGroupValidation from '../shared/FormGroupValidation';
import { useElementIds } from '../../../helpers/hooks/useElementIds';

import { FormControlFeedback } from '../form-control';

const FileUpload = React.forwardRef(
	(
		{
			id: propsId,
			label,
			'aria-label': ariaLabel,
			noFilesText = 'No file(s) chosen',
			tooltip,
			dirty = false,
			accept = sharedDefaultProps.accept,
			blackList = BLACKLIST_FILE_EXTENSIONS,
			maxSize = FIFTEEN_MEGABYTE,
			duplicateFile = ERROR_MESSAGE.duplicateFile,
			disallowedFileType = ERROR_MESSAGE.disallowedFileType,
			fileTooLarge = ERROR_MESSAGE.fileTooLarge,
			tooManyFiles = ERROR_MESSAGE.tooManyFiles,
			fileEmpty = ERROR_MESSAGE.fileEmpty,
			customFileList,
			hideFileList = false,
			preview = false,
			stateReducer,
			fileListFileTypeColumnText,
			fileListFileNameColumnText,
			groupClassName,
			inline,
			required,
			validators = [],
			helptext,
			requiredFieldMessage = FormGroupValidation.defaultProps.requiredFieldMessage,
			internalProps,
			...props
		}: FileUploadProps,
		ref: ForwardedRef<FileUploadRef>
	) => {
		const dragAndDropRef = React.useRef<HTMLInputElement>(null);

		const { data, dispatch, addFiles, removeFiles, formState } =
			internalProps ??
			useFileUploadInternal({
				name: props.name,
				required: required ?? false,
				blackList,
				accept,
				maxSize,
				dirty,
				stateReducer,
				multiple: props.multiple ?? sharedDefaultProps.multiple,
				disabled: props.disabled,
				errorMessages: {
					duplicateFile,
					disallowedFileType,
					fileTooLarge,
					tooManyFiles,
					fileEmpty
				},
				validators
			});

		const { id } = useElementIds({ prefix: 'fileupload', id: propsId });
		const helpblockId = helptext ? id + '_helptext' : null;
		const formGroupClasses = ['mb-3'];
		if (inline) {
			formGroupClasses.push('row', 'gx-2');
		}

		useImperativeHandle(ref, () => ({
			element: dragAndDropRef.current ?? undefined,
			removeFiles,
			addFiles
		}));

		const getErrorMessage = () => {
			if (props.disabled) return null;

			if (required && data.fileList.length === 0) {
				return `${label || ariaLabel} ${requiredFieldMessage}`;
			}

			return null;
		};
		const errorMessage = getErrorMessage();
		const isInvalid = data.isInvalid || fieldHasErrors(props.name, formState?.errors) || (Array.isArray(errorMessage) && errorMessage.length > 0);

		return (
			<FormGroup className={formGroupClasses.join(' ')}>
				<Label id={id ? id + '_label' : null} required={required} tooltip={tooltip} htmlFor={id} column={inline}>
					{label}
				</Label>
				<InlineHelper inline={inline}>
					<DragAndDrop
						id={id}
						ref={dragAndDropRef}
						fileUpload={{
							data,
							dispatch,
							addFiles
						}}
						accept={accept}
						maxSize={maxSize}
						isInvalid={isInvalid}
						aria-label={label ? undefined : ariaLabel || undefined}
						{...props}
					/>

					{!hideFileList ? (
						customFileList ? (
							customFileList({ data, dispatch })
						) : (
							<FileUploadFilesList
								isInvalid={isInvalid}
								fileListFileNameColumnText={fileListFileNameColumnText}
								fileListFileTypeColumnText={fileListFileTypeColumnText}
								fileList={data.fileList}
								disabled={props.disabled}
								preview={preview}
								dispatch={dispatch}
								noFilesText={noFilesText}
							/>
						)
					) : null}

					<Helpblock id={helpblockId}>{helptext}</Helpblock>

					{!props.suppressValidationMessage && (required || validators.length > 0) ? (
						<FormControlFeedback type="invalid">{getErrorMessage()}</FormControlFeedback>
					) : null}

					{data.showErrorAlert ? (
						<FormControlFeedback type="invalid" style={{ display: 'block' }}>
							<ul className="list-group" style={{ listStyle: 'none' }}>
								{data.errorList.map((error, index) => {
									return <li key={index}>{error}</li>;
								})}
							</ul>
						</FormControlFeedback>
					) : null}
				</InlineHelper>
			</FormGroup>
		);
	}
);
FileUpload.defaultProps = {
	...inputDefaultProps
};
FileUpload.displayName = 'FileUpload';

export default FileUpload;
